import { FormattedMessage } from "react-intl";
import { HStack, VStack, StackDivider, Text, Alert, AlertIcon } from "@chakra-ui/react";
import { useCurrencyFormatter } from "components/FormattedMoney/useCurrencyFormatter";

const parseBankValues = (values) => {
  if (typeof values !== "object") return null;
  const institutionName = values.payoutDestination?.institution_name;
  const displayString = values.payoutDestination?.display_string;
  const payoutRequested = values.payoutRails?.payout_requested;
  const payoutFee = values.payoutRails?.payout_fee;
  const payoutTotal = values.payoutRails?.payout_total;
  const currency = values.payoutRails?.currency || "USD";

  let isValidPayload = true;
  if (!institutionName && !displayString) isValidPayload = false;
  if (!Number.isInteger(payoutRequested)) isValidPayload = false;
  if (!Number.isInteger(payoutFee)) isValidPayload = false;
  if (!Number.isInteger(payoutTotal)) isValidPayload = false;

  if (isValidPayload) {
    return {
      institutionName,
      displayString,
      payoutRequested,
      payoutFee,
      payoutTotal,
      currency
    };
  } else {
    return null;
  }
};

const BankPayoutReview = ({ values }) => {
  const data = parseBankValues(values);
  const bankPayoutDisplayCurrency = (data && data.currency) || "USD";
  const formatter = useCurrencyFormatter(bankPayoutDisplayCurrency);
  const fromCurrencyFormatter = useCurrencyFormatter(values?.currency || "USD"); // since bank payout option data should always be in USD, this is to handle if the original currency was not USD (as in, virtual currencies)
  if (!data) {
    return (
      <Alert status="error">
        <AlertIcon />
        <FormattedMessage defaultMessage="Missing required data." description="Alert title" />
      </Alert>
    );
  }
  return (
    <VStack
      data-testid="bank-payout-confirm-and-review"
      width="100%"
      align="flex-start"
      p="4"
      borderWidth="1px"
      borderRadius="4px"
      gap="2"
      bgColor={"background"}
      boxShadow="base"
      divider={<StackDivider />}
    >
      <HStack justify="space-between" width="100%">
        <Text fontSize="sm" color="secondaryText">
          <FormattedMessage
            defaultMessage="Destination:"
            description="label for where transfered money will go to"
          />
        </Text>
        <VStack align="flex-end" spacing="0">
          <Text size="sm">{data.institutionName || null}</Text>
          <Text size="sm">{data.displayString || null}</Text>
        </VStack>
      </HStack>
      <HStack justify="space-between" width="100%">
        <Text fontSize="sm" color="secondaryText">
          <FormattedMessage
            defaultMessage="Requested amount:"
            description="label for amount requested in payout"
          />
        </Text>
        <HStack>
          <Text size="sm">{formatter(data.payoutRequested)}</Text>
          {(values.currency !== bankPayoutDisplayCurrency) && values.amount && (
            <Text as="i" fontSize="sm" color="secondaryText">&nbsp;({fromCurrencyFormatter(values.amount)})</Text>
          )}
        </HStack>
      </HStack>
      <HStack justify="space-between" width="100%">
        <VStack align="flex-start">
          <Text fontSize="sm" color="secondaryText">
            <FormattedMessage defaultMessage="Fee:" description="label for fee amount with payout" />
            <sup>*</sup>
          </Text>
          <Text as="i" fontSize="0.55em" color="secondaryText">
            <sup>*&nbsp;</sup>
            <FormattedMessage
              defaultMessage="Estimated. Fees calculated at time of payout execution."
              description="fee estimation description"
            />
          </Text>
        </VStack>
        <Text size="sm">~{formatter(data.payoutFee)}</Text>
      </HStack>
      <HStack justify="space-between" width="100%">
        <Text fontWeight="700" fontSize="sm" color="secondaryText">
          <FormattedMessage
            defaultMessage="Payout total:"
            description="label for payout amount including fees"
          />
        </Text>
        <Text fontWeight="700" size="sm">
          {formatter(data.payoutTotal)}
        </Text>
      </HStack>
    </VStack>
  );
};

export default BankPayoutReview;
